import { t } from "i18next";
import React, { useState, useEffect, useRef } from "react";
import { Trans } from "react-i18next";
import UnitsSchema from "../../schemas/units.schema";
import { validate } from "../../utils/ValidationHelpers";
import ValidationError from "../Shared/ValidationError";
import BaseModal from "./BaseModal";
import Select from "react-select";
import EquationBuilderWidget from "../Widgets/EquationBuilderWidget";
import LookupsAPI from "../../API/lookups.api";
import { getReactSelectStyling } from "../../utils/ReactSelectStyles";

const BASE_VARIABLE = [{ label: "BASE", value: "BASE" }];

const ConversionUnitModal = ({
    isVisible,
    setIsVisible,
    editConvertionUnitAction,
    addConvertionUnitAction,
    conversionUnit,
    setConversionUnit,
}) => {
    const [error, setError] = useState({});
    const [isFormValid, setIsFormValid] = useState(false);
    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);
    const [isEquationValid, setIsEquationValid] = useState(false);
    const [initialEquation, setInitialEquation] = useState("");
    const [unitsLookup, setUnitsLookup] = useState([]);

    const [isEdit, setIsEdit] = useState(false);

    const widgetRef = useRef();

    useEffect(() => {
        if (conversionUnit.baseUnitId) getAllowedUnitsLookup();
    }, [conversionUnit.baseUnitId]);

    useEffect(() => {
        validate(
            conversionUnit,
            UnitsSchema.ConversionUnit,
            setIsFormValid,
            setError
        );
    }, [conversionUnit]);

    useEffect(() => {
        if (!isVisible) resetForm();
        else if (
            isVisible &&
            (conversionUnit?.formattedEquation || conversionUnit?.equation)
        ) {
            getExistingValues();
            setIsEdit(true);
        } else {
            setIsEdit(false);
        }
    }, [isVisible]);

    const getAllowedUnitsLookup = async () => {
        const result = await LookupsAPI.getAllowedUnitConversions(
            conversionUnit.baseUnitId
        );
        setUnitsLookup(result);
    };

    const resetForm = () => {
        setConversionUnit({});
        setError({});
        setIsFormValid(false);
        setIsEquationValid(false);
        setIsFormSubmitted(false);
        setInitialEquation("");
        setIsFormSubmitting(false);
    };

    const getExistingValues = () => {
        setInitialEquation(
            conversionUnit?.formattedEquation || conversionUnit?.equation
        );
        setConversionUnit((prevData) => ({
            ...prevData,
            conversionUnit: {
                value: prevData.id,
                label: prevData.unit,
            },
        }));
    };

    const formatAddConversionUnitPayload = () => {
        return {
            baseUnitId: conversionUnit.baseUnitId,
            conversionUnitId: conversionUnit.conversionUnit.value,
            description: conversionUnit.description,
            equation: widgetRef.current.getStringEquation()?.value,
        };
    };

    const formatEditConversionUnitPayload = () => {
        return {
            id: conversionUnit.id,
            description: conversionUnit.description,
            equation: widgetRef.current.getStringEquation()?.value,
        };
    };

    const submitAction = () => {
        setIsFormSubmitting(true);
        setIsFormSubmitted(true);
        if (isFormValid && isEquationValid) {
            isEdit
                ? editConvertionUnitAction(formatEditConversionUnitPayload())
                : addConvertionUnitAction(formatAddConversionUnitPayload());
        }
        setIsFormSubmitting(false);
        return;
    };

    const returnErrorMessages = (propName) => {
        return isFormSubmitted && error[propName];
    };

    const handleInputChange = (e) => {
        setConversionUnit((prevParams) => {
            return {
                ...prevParams,
                [e.target.name]: e.target.value,
            };
        });
    };

    const handleSelectChange = (value, action) => {
        setConversionUnit((prevParams) => {
            return {
                ...prevParams,
                [action.name]: value,
            };
        });
    };

    const renderChildren = () => (
        <>
            <div
                className={`input-group ${
                    returnErrorMessages("conversionUnit")
                        ? "invalid-warning-input"
                        : ""
                }`}
            >
                <label className="uppercase required">
                    <Trans i18nKey="conversionUnit">Conversion Unit</Trans>
                </label>
                <Select
                    options={unitsLookup}
                    isDisabled={isEdit}
                    name="conversionUnit"
                    onChange={handleSelectChange}
                    value={conversionUnit?.conversionUnit || ""}
                    styles={getReactSelectStyling({
                        showError: !!returnErrorMessages("conversionUnit"),
                    })}
                    placeholder={t("selectPlaceholder", { entity: t("unit") })}
                />
                <ValidationError
                    errorMessage={returnErrorMessages("conversionUnit")}
                />
            </div>
            <div
                className={`input-group ${
                    returnErrorMessages("description")
                        ? "invalid-warning-input"
                        : ""
                }`}
            >
                <label className="uppercase">
                    <Trans i18nKey="description">Description</Trans>
                </label>
                <textarea
                    className="input-element"
                    name="description"
                    type="text"
                    onChange={handleInputChange}
                    value={conversionUnit?.description || ""}
                    placeholder={t("description")}
                ></textarea>
                <ValidationError
                    errorMessage={returnErrorMessages("description")}
                />
            </div>
            <div
                className={`input-group ${
                    returnErrorMessages("conversionUnit")
                        ? "invalid-warning-input"
                        : ""
                }`}
            >
                <label className="uppercase required">
                    <Trans i18nKey="equation">Equation</Trans>
                </label>
                <EquationBuilderWidget
                    ref={widgetRef}
                    equation={initialEquation}
                    isFormSubmitted={isFormSubmitted}
                    isEquationValid={isEquationValid}
                    setIsEquationValid={setIsEquationValid}
                    variables={BASE_VARIABLE}
                />
            </div>
        </>
    );

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isFormSubmitting}
                title={t(isEdit ? "editTitle" : "addTitle", {
                    entity: t("unit"),
                })}
                action={{
                    submitAction: submitAction,
                    disabled:
                        isFormSubmitted && !(isFormValid && isEquationValid),
                }}
            >
                {renderChildren()}
            </BaseModal>
        </>
    );
};

export default ConversionUnitModal;
